import { useEffect, useRef, useState } from "react";
import { api, fmtDateTime } from "../lib/api";
import { supabase } from "../lib/supabase";
import { PageHeader } from "../components/UI";
import { useAuth } from "../lib/auth";
import { PaperPlaneRight } from "@phosphor-icons/react";

export default function Messages() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [users, setUsers] = useState([]);
  const endRef = useRef(null);
  const channelRef = useRef(null);
  const retryRef = useRef({ timer: null, attempts: 0 });

  const teardown = () => {
    if (retryRef.current.timer) { clearTimeout(retryRef.current.timer); retryRef.current.timer = null; }
    if (channelRef.current) { supabase.removeChannel(channelRef.current); channelRef.current = null; }
  };

  useEffect(() => {
    // iter 73.7 — RLS hardening: tolerate `r.data` being null/undefined
    // (e.g. if backend role switches to RLS-enforced and a tenant has
    // zero channels). All downstream `.map` / `.length` calls assume
    // non-null arrays.
    api.get("/channels").then((r) => {
      const list = r.data || [];
      setChannels(list);
      if (list.length && !active) setActive(list[0]);
    });
    api.get("/users").then((r) => setUsers(r.data || []));
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/channels/${active.id}/messages`).then((r) => setMessages(r.data || []));

    const subscribe = () => {
      teardown();
      const ch = supabase
        .channel(`messages:${active.id}`)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "messages", filter: `channel_id=eq.${active.id}` },
          (payload) => {
            const m = payload.new;
            setMessages((prev) => prev.find((x) => x.id === m.id) ? prev : [...prev, {
              id: m.id, channel_id: m.channel_id, sender_id: m.sender_id,
              sender_name: users.find((u) => u.id === m.sender_id)?.name || "User",
              content: m.content, created_at: m.created_at,
            }]);
          }
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            retryRef.current.attempts = 0;
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s with ±20% jitter
            const attempt = retryRef.current.attempts++;
            const base = Math.min(30000, 1000 * Math.pow(2, attempt));
            const jitter = base * (0.8 + Math.random() * 0.4);
            retryRef.current.timer = setTimeout(subscribe, jitter);
          }
        });
      channelRef.current = ch;
    };
    subscribe();
    return teardown;
  }, [active, users]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !active) return;
    const content = input;
    setInput("");
    try {
      const r = await api.post("/messages", { channel_id: active.id, content });
      // Optimistic update (Realtime will dedupe by id)
      setMessages((prev) => prev.find((x) => x.id === r.data.id) ? prev : [...prev, r.data]);
    } catch { setInput(content); }
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || (id === user?.id ? user.name : "User");

  return (
    <div className="p-0 h-screen flex" data-testid="messages-page">
      <div className="w-64 border-r border-border bg-secondary/30 p-4">
        <div className="label-xs mb-3">CHANNELS</div>
        {channels.map((c) => (
          <button key={c.id} className={`w-full text-left px-3 py-2 text-sm rounded-sm ${active?.id === c.id ? "bg-primary text-white" : "hover:bg-secondary"}`} onClick={() => setActive(c)} data-testid={`channel-${c.id}`}>
            # {c.name}
          </button>
        ))}
      </div>
      <div className="flex-1 flex flex-col">
        <div className="border-b border-border p-4 flex items-baseline gap-3">
          <span className="heading text-xl">#{active?.name || "general"}</span>
          <span className="label-xs">REAL-TIME · SUPABASE</span>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && <div className="text-muted-foreground text-center py-12">No messages yet — say hi 👋</div>}
          {messages.map((m) => (
            <div key={m.id} className="flex gap-3" data-testid={`msg-${m.id}`}>
              <div className="w-8 h-8 bg-primary text-white flex items-center justify-center mono text-xs flex-shrink-0">{(m.sender_name || userName(m.sender_id))[0]?.toUpperCase()}</div>
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm">{m.sender_name || userName(m.sender_id)}</span>
                  <span className="text-xs text-muted-foreground mono">{fmtDateTime(m.created_at)}</span>
                </div>
                <div className="text-sm">{m.content}</div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-border p-4 flex gap-2">
          <input className="input" placeholder={`Message #${active?.name || ""}...`} value={input} onChange={(e) => setInput(e.target.value)} data-testid="message-input" />
          <button type="submit" className="btn btn-primary" data-testid="send-message-btn"><PaperPlaneRight size={14} /></button>
        </form>
      </div>
    </div>
  );
}
